import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { getUpdatedImageUrlWithSize } from "../utils/helpers";
import * as Unicons from "@iconscout/react-unicons";
import debounce from "lodash/debounce";

export default function SearchProjectWithDropDown({
  OnChangeSearchInput,
  allProjects,
}) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [projectData, setProjectData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  const fetchProjects = async (page = 1, search = "", isNewSearch = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases`,
        {
          params: { page, limit: 10, search },
        },
      );
      const newProjects = response.data.results || [];

      setProjectData((prevProjects) =>
        isNewSearch ? newProjects : [...prevProjects, ...newProjects],
      );
      setCurrentPage(response.data.currentPage);

      if (
        response.data.currentPage >= response.data.totalPages ||
        newProjects.length === 0
      ) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error("Error fetching projects in dropdown:", error);
    }
  };

  // Debounced search trigger to fetch pages on type
  const debouncedFetchRef = useRef(
    debounce((search) => {
      fetchProjects(1, search, true);
    }, 500),
  );

  useEffect(() => {
    if (showDropdown) {
      debouncedFetchRef.current(searchInput);
    }
    return () => debouncedFetchRef.current.cancel();
  }, [searchInput, showDropdown]);

  const loadMoreProjects = () => {
    fetchProjects(currentPage + 1, searchInput, false);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleProjectClick = (projectId) => {
    navigate(`/project-showcase/${projectId}`, { state: { allProjects } });
    setSearchInput("");
    if (OnChangeSearchInput) {
      OnChangeSearchInput("");
    }
    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} className="w-full relative">
      <input
        type="text"
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          setShowDropdown(e.target.value.length > 0);
          if (OnChangeSearchInput) {
            OnChangeSearchInput(e.target.value);
          }
        }}
        onFocus={() => {
          if (searchInput.length > 0) {
            setShowDropdown(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        className="block p-2.5 w-full z-20 text-sm bg-white rounded-lg border border-gray-300 focus:border-gray-300 focus:ring-0"
        placeholder="Search projects..."
        autoComplete="off"
      />
      <button
        type="button"
        className="absolute top-[3px] right-0.5 p-2 text-sm rounded-r-lg bg-white"
      >
        <Unicons.UilSearch size="19" />
      </button>

      {showDropdown && (
        <div
          id="project-scrollable-div"
          className="absolute top-full mt-1 w-full left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
        >
          <InfiniteScroll
            dataLength={projectData.length}
            next={loadMoreProjects}
            hasMore={hasMore}
            scrollableTarget="project-scrollable-div"
            loader={
              <div className="text-center py-2 text-sm text-gray-500">
                Loading more projects...
              </div>
            }
          >
            {projectData.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {projectData.map((project) => (
                  <li
                    key={project._id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                    onClick={() => handleProjectClick(project._id)}
                  >
                    <img
                      src={getUpdatedImageUrlWithSize(
                        `${process.env.REACT_APP_BACK_URL}/api/minio/${project.thumbNail}`,
                        [1, 1, 1],
                      )}
                      alt={project.title}
                      className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-gray-800 text-sm truncate">
                        {project.title}
                      </span>
                      {project.industry && (
                        <span className="text-gray-400 text-xs truncate">
                          {project.industry}
                        </span>
                      )}
                    </div>
                    {project.isLive === false && (
                      <span className="   text-xs text-white bg-red-600 py-1 px-2 rounded-full z-[9]">
                        Not Live
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-gray-400 text-sm text-center">
                No projects found.
              </div>
            )}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}
